<script setup lang="ts">
import {computed, onMounted, ref} from 'vue';
import {teamsBackend, type Team} from '@/backend/teams.backend';
import {peopleBackend} from '@/backend/people.backend';
import {type Person} from '@/types/DataTypes';
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import Panel from 'primevue/panel';
import Listbox from 'primevue/listbox';
import Popover from 'primevue/popover';
import hOSAvatar from '@/components/hOSAvatar.vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import {useToast} from 'primevue/usetoast';
import {useConfirm} from 'primevue/useconfirm';
import ConfirmDialog from 'primevue/confirmdialog';
import {useRouter} from 'vue-router';

interface Props {
  team: Team;
}

interface Emits {
  (e: 'teamUpdated'): void;
}

const {team} = defineProps<Props>();
const emit = defineEmits<Emits>();
const router = useRouter();
const toast = useToast();
const confirm = useConfirm();

const loading = ref(false);
const allUsers = ref<Person[]>([]);
const teamMembers = ref<any[]>([]);
const selectedPeople = ref<any[]>([]);
const selectedUsersToAdd = ref<Person[]>([]);
const addUsersPopover = ref();

// Form data
const teamForm = ref({
  name: team.name,
  leadUserId: team.leadUserId || null,
  slackChannel: team.slackChannel || '',
  isActive: team.isActive
});

const leadUsers = computed(() => {
  return allUsers.value.map(user => ({
    label: user.name,
    value: user.id
  }));
});

const usersNotInTeam = computed(() => {
  const memberIds = teamMembers.value.map(m => m.id);
  return allUsers.value.filter(u => !memberIds.includes(u.id));
});

onMounted(async () => {
  await loadData();
});

async function loadData() {
  loading.value = true;
  try {
    [allUsers.value, teamMembers.value] = await Promise.all([
      peopleBackend.getDirectory(),
      loadTeamMembers()
    ]);
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to load data',
      life: 3000
    });
  } finally {
    loading.value = false;
  }
}

async function loadTeamMembers(): Promise<any[]> {
  const updatedTeam = await teamsBackend.getTeam(team.id);
  return updatedTeam.users || [];
}

async function updateTeam() {
  if (!teamForm.value.name.trim()) {
    toast.add({
      severity: 'warn',
      summary: 'Validation Error',
      detail: 'Team name is required',
      life: 3000
    });
    return;
  }

  loading.value = true;
  try {
    const updatedTeam = await teamsBackend.updateTeam(team.id)(teamForm.value);
    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Team updated successfully',
      life: 3000
    });
    
    // If the team name changed, the slug will have changed - navigate to new URL
    if (updatedTeam.slug !== team.slug) {
      await router.push(`/teams/${updatedTeam.slug}/edit`);
    }
    
    emit('teamUpdated');
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to update team',
      life: 3000
    });
  } finally {
    loading.value = false;
  }
}

function showAddPeoplePopover(event: Event) {
  selectedUsersToAdd.value = [];
  addUsersPopover.value.show(event);
}

async function addPeople() {
  if (selectedUsersToAdd.value.length === 0) return;

  loading.value = true;
  try {
    const userIds = selectedUsersToAdd.value.map(u => u.id);
    await teamsBackend.assignUsersToTeam(team.id)({ userIds });
    
    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: `Added ${selectedUsersToAdd.value.length} member(s) to the team`,
      life: 3000
    });
    
    selectedUsersToAdd.value = [];
    addUsersPopover.value.hide();
    await loadTeamMembers();
    teamMembers.value = await loadTeamMembers();
    emit('teamUpdated');
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to add team members',
      life: 3000
    });
  } finally {
    loading.value = false;
  }
}

async function removePeople() {
  if (selectedPeople.value.length === 0) return;

  loading.value = true;
  try {
    const userIds = selectedPeople.value.map(u => u.id);
    await teamsBackend.removeUsersFromTeams({ userIds });
    
    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: `Removed ${selectedPeople.value.length} member(s) from the team`,
      life: 3000
    });
    
    selectedPeople.value = [];
    teamMembers.value = await loadTeamMembers();
    emit('teamUpdated');
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to remove team members',
      life: 3000
    });
  } finally {
    loading.value = false;
  }
}

function deactivateTeam() {
  confirm.require({
    message: `Are you sure you want to deactivate "${team.name}"? Team members will remain in the team but the team will be hidden from active lists.`,
    header: 'Confirm Deactivation',
    icon: 'pi pi-exclamation-triangle',
    acceptClass: 'p-button-danger',
    rejectClass: 'p-button-secondary p-button-outlined',
    accept: async () => {
      loading.value = true;
      try {
        await teamsBackend.deactivateTeam(team.id);
        toast.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Team deactivated successfully',
          life: 3000
        });
        emit('teamUpdated');
        router.push('/teams');
      } catch (error) {
        toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to deactivate team',
          life: 3000
        });
      } finally {
        loading.value = false;
      }
    }
  });
}
</script>

<template>
  <div class="grid grid-cols-1 xl:grid-cols-2 gap-6">
    <ConfirmDialog />
    
    <!-- Left Column -->
    <div class="space-y-6">
      <!-- Team Details Form -->
      <Panel header="Team Details">
        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium mb-2">Team Name *</label>
            <InputText 
              v-model="teamForm.name" 
              placeholder="Enter team name"
              class="w-full"
            />
          </div>
          
          <div>
            <label class="block text-sm font-medium mb-2">Team Lead</label>
            <Select 
              v-model="teamForm.leadUserId"
              :options="leadUsers"
              optionLabel="label"
              optionValue="value"
              placeholder="Select team lead"
              class="w-full"
              showClear
            />
          </div>
          
          <div>
            <label class="block text-sm font-medium mb-2">Slack Channel</label>
            <InputText 
              v-model="teamForm.slackChannel" 
              placeholder="#team-channel"
              class="w-full"
            />
          </div>
          
          <div class="flex items-center gap-2">
            <Checkbox v-model="teamForm.isActive" binary input-id="isActive" />
            <label for="isActive">Active team</label>
          </div>

          <div class="flex gap-2 justify-end pt-4">
            <Button 
              label="Save Changes" 
              icon="pi pi-save"
              @click="updateTeam"
              :loading="loading"
            />
          </div>
        </div>
      </Panel>

      <!-- Danger Zone -->
      <Panel header="Danger Zone" class="border-red-500">
        <div class="flex justify-between items-center">
          <div>
            <h3 class="font-semibold text-red-600 dark:text-red-400">Deactivate Team</h3>
            <p class="text-sm text-gray-600 dark:text-gray-400">
              Deactivate this team. Team members will remain in the team but the team will be hidden.
            </p>
          </div>
          <Button 
            label="Deactivate Team" 
            severity="danger"
            outlined
            @click="deactivateTeam"
            :disabled="loading || !teamForm.isActive"
          />
        </div>
      </Panel>
    </div>

    <!-- Right Column -->
    <div>
      <!-- Team Members Management -->
      <Panel header="Team Members">
        <template #header>
          <div class="flex gap-2 items-center">
            <span class="p-panel-title">Team Members ({{ teamMembers.length }})</span>
            <Button 
              severity="secondary" 
              rounded 
              outlined 
              icon="pi pi-user-plus" 
              @click="showAddPeoplePopover"
              v-tooltip.top="'Add members'"
              :disabled="loading"
            />
            <Button 
              severity="danger" 
              rounded 
              outlined 
              icon="pi pi-user-minus" 
              @click="removePeople"
              v-tooltip.top="'Remove selected members'"
              :disabled="selectedPeople.length === 0 || loading"
            />
          </div>
        </template>

        <Popover ref="addUsersPopover" @hide="selectedUsersToAdd = []">
          <div class="space-y-4 p-2">
            <Listbox 
              v-model="selectedUsersToAdd"
              :options="usersNotInTeam"
              optionLabel="name"
              placeholder="Add user"
              list-style="max-height: 30rem"
              multiple 
              filter
              class="w-96"
            >
              <template #option="slotProps">
                <div class="flex items-center gap-2">
                  <hOSAvatar :user="slotProps.option" size="small" />
                  <span>{{ slotProps.option.name }}</span>
                </div>
              </template>
            </Listbox>
            <div class="flex justify-end">
              <Button 
                label="Add" 
                @click="addPeople"
                :disabled="selectedUsersToAdd.length === 0 || loading"
              />
            </div>
          </div>
        </Popover>

        <DataTable 
          :value="teamMembers"
          v-model:selection="selectedPeople"
          selection-mode="multiple"
          striped-rows
          scrollable 
          scroll-height="40rem"
          sort-field="name"
          :sort-order="1"
        >
          <Column selection-mode="multiple" style="width: 3em" />
          <Column style="width: 3rem">
            <template #body="slotProps">
              <hOSAvatar :user="slotProps.data" />
            </template>
          </Column>
          <Column field="name" header="Name" sortable />
          <Column field="title" header="Title" />
        </DataTable>
      </Panel>
    </div>
  </div>
</template>
